'use client'

import { popModal } from '@/components/modals'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ResponsiveModal } from '@/components/ui/responsive-modal'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import type { IWorkspaceWithSubscription } from '@/hooks/subscriptions/use-workspaces'
import {
  type IProjectFeatures,
  type IWorkspaceCompatibility,
  analyzeProjectFeatures,
  normalizePlanTier,
  validateMigrationCompatibility,
} from '@/lib/migration-validation'
import type { Project } from '@/lib/types'
import { cn } from '@/lib/utils'
import {
  AlertCircle,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Search,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

interface IMigrateProjectModalProps {
  project: Project
  currentWorkspaceId: string
  onSuccess?: () => void
}

/**
 * Modal for migrating a project to a different workspace
 * @param project - The project to migrate
 * @param currentWorkspaceId - The current workspace ID
 * @param onSuccess - Optional callback called after successful migration
 */
export default function MigrateProjectModal({
  project,
  currentWorkspaceId,
  onSuccess,
}: IMigrateProjectModalProps) {
  const t = useTranslations('migrate-project-modal')

  const [open, setOpen] = useState(true)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [productsCount, setProductsCount] = useState<number | null>(null)
  const [loadingProductsCount, setLoadingProductsCount] = useState(true)
  const [productsCountError, setProductsCountError] = useState(false)

  const { workspaces, loading: loadingWorkspaces } = useWorkspaces()

  const projectId = project._id.toString()
  const projectName = project.project_name

  /**
   * Fetch products count on mount
   */
  useEffect(() => {
    const fetchProductsCount = async () => {
      try {
        const response = await fetch(
          `/api/me/project/${projectId}/products-count`,
        )
        const result = await response.json()
        if (result.success) {
          setProductsCount(result.data.productsCount)
          setProductsCountError(false)
        } else {
          // Backend returned success: false - treat as error
          console.error('Failed to fetch products count:', result.message)
          setProductsCountError(true)
          setProductsCount(0) // Default to 0 to prevent bypass
        }
      } catch (error) {
        console.error('Error fetching products count:', error)
        setProductsCountError(true)
        setProductsCount(0) // Default to 0 to prevent bypass
      } finally {
        setLoadingProductsCount(false)
      }
    }

    fetchProductsCount()
  }, [projectId])

  /**
   * Analyze project features
   */
  const projectFeatures: IProjectFeatures | null = useMemo(() => {
    if (productsCount === null) {
      return null
    }
    return analyzeProjectFeatures(project, productsCount)
  }, [project, productsCount])

  /**
   * Validate compatibility for each workspace
   */
  const workspaceCompatibility = useMemo(() => {
    if (!projectFeatures) {
      return {}
    }

    const compatibility: Record<string, IWorkspaceCompatibility> = {}

    workspaces.forEach((workspace) => {
      const workspaceId = workspace._id.toString()
      if (workspaceId === currentWorkspaceId) {
        return // Skip current workspace
      }

      const workspacePlan = normalizePlanTier(workspace.limits.plan)
      const hasAvailableDomains = workspace.limits.can_create_more

      compatibility[workspaceId] = validateMigrationCompatibility(
        projectFeatures,
        workspacePlan,
        hasAvailableDomains,
      )
    })

    return compatibility
  }, [projectFeatures, workspaces, currentWorkspaceId])

  /**
   * Filter and sort workspaces based on search term
   */
  const filteredWorkspaces = useMemo(() => {
    let result = workspaces

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = workspaces.filter((workspace) => {
        return workspace.name.toLowerCase().includes(search)
      })
    }

    // Sort by compatibility first, then by update_time
    return [...result].sort((a, b) => {
      const aId = a._id.toString()
      const bId = b._id.toString()

      // Skip current workspace in sorting
      if (aId === currentWorkspaceId) {
        return 1
      }
      if (bId === currentWorkspaceId) {
        return -1
      }

      const aCompat = workspaceCompatibility[aId]
      const bCompat = workspaceCompatibility[bId]

      // Compatible workspaces come first
      if (aCompat?.compatible && !bCompat?.compatible) {
        return -1
      }
      if (!aCompat?.compatible && bCompat?.compatible) {
        return 1
      }

      // Within same compatibility group, sort by update_time
      const timeA = new Date(a.update_time).getTime()
      const timeB = new Date(b.update_time).getTime()
      return timeB - timeA
    })
  }, [workspaces, searchTerm, workspaceCompatibility, currentWorkspaceId])

  /**
   * Get the current workspace object
   */
  const currentWorkspace = useMemo(() => {
    return workspaces.find((w) => {
      return w._id.toString() === currentWorkspaceId
    })
  }, [currentWorkspaceId, workspaces])

  /**
   * Get the selected workspace object
   */
  const selectedWorkspace = useMemo(() => {
    if (!selectedWorkspaceId) {
      return null
    }
    return workspaces.find((w) => {
      return w._id.toString() === selectedWorkspaceId
    })
  }, [selectedWorkspaceId, workspaces])

  /**
   * Handle workspace selection
   */
  const handleSelectWorkspace = (workspaceId: string) => {
    // Don't allow selecting the current workspace
    if (workspaceId === currentWorkspaceId) {
      toast.error(t('project_already_in_this_workspace'))
      return
    }

    // Check compatibility
    const compatibility = workspaceCompatibility[workspaceId]
    if (compatibility && !compatibility.compatible) {
      toast.error(t('this_workspace_is_not_compatible_with_this_project'))
      return
    }

    setSelectedWorkspaceId(workspaceId)
  }

  /**
   * Handle migration
   */
  const handleMigrate = async () => {
    if (!showConfirmation) {
      setShowConfirmation(true)
      return
    }

    try {
      setLoading(true)

      const response = await fetch(`/api/me/project/${projectId}/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetWorkspaceId: selectedWorkspaceId,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.message || t('failed_to_migrate_project'))
      }

      toast.success(t('project_migrated_successfully'))
      onSuccess?.()
      popModal()
    } catch (error) {
      console.error('Error migrating project:', error)
      toast.error(
        error instanceof Error ? error.message : t('failed_to_migrate_project'),
      )
    } finally {
      setLoading(false)
    }
  }

  /**
   * Get plan badge color matching workspace-selector-modal
   */
  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'platinum':
        return 'from-amber-200 via-yellow-300 to-amber-200 text-gray-900'
      case 'premium':
        return 'from-purple-500 to-blue-500 text-white'
      case 'basic':
        return 'bg-blue-500 text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  /**
   * Render workspace card (matches workspace-selector-modal style)
   */
  const renderWorkspaceCard = (workspace: IWorkspaceWithSubscription) => {
    const workspaceId = workspace._id.toString()
    const isSelected = workspaceId === selectedWorkspaceId
    const isCurrent = workspaceId === currentWorkspaceId
    const compatibility = workspaceCompatibility[workspaceId]

    const isIncompatible = compatibility && !compatibility.compatible

    return (
      <button
        key={workspaceId}
        type="button"
        onClick={() => {
          return handleSelectWorkspace(workspaceId)
        }}
        disabled={isCurrent || isIncompatible}
        className={cn(
          'w-full text-left p-4 rounded-lg border-2 transition-all',
          (isCurrent || isIncompatible) && 'cursor-not-allowed',
          !isCurrent && !isIncompatible && 'hover:border-primary/30',
          isSelected ? 'border-primary bg-primary/10' : 'border-border bg-card',
        )}
      >
        {/* Header: Workspace name, plan, selection indicator */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="size-4 text-muted-foreground flex-shrink-0" />
              <h3 className="text-sm font-semibold truncate">
                {workspace.name || workspaceId}
              </h3>
              <div
                className={cn(
                  'inline-block text-xs px-2 py-0.5 rounded font-semibold',
                  workspace.limits.plan !== 'free'
                    ? `bg-gradient-to-r ${getPlanBadgeColor(workspace.limits.plan)}`
                    : getPlanBadgeColor(workspace.limits.plan),
                )}
              >
                {workspace.limits.plan.charAt(0).toUpperCase() +
                  workspace.limits.plan.slice(1)}
              </div>
              {isCurrent && (
                <span className="text-xs text-muted-foreground">(Current)</span>
              )}
              {isSelected && !isCurrent && (
                <Check className="size-4 text-green-400 flex-shrink-0" />
              )}
              {!isCurrent && compatibility && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      {compatibility.compatible ? (
                        <CheckCircle2 className="size-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="size-4 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {compatibility.compatible ? (
                      <p>{t('compatible_with_this_project')}</p>
                    ) : (
                      <div className="max-w-xs">
                        <p className="font-semibold mb-1">
                          {t('not_compatible')}:
                        </p>
                        <ul className="list-disc pl-4 space-y-1">
                          {compatibility.reasons.map((reason) => {
                            return (
                              <li key={reason}>{t(`reasons.${reason}`)}</li>
                            )
                          })}
                        </ul>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {workspace.project_count || 0} project
              {workspace.project_count !== 1 ? 's' : ''}
            </p>
            {isIncompatible && compatibility.reasons.length > 0 && (
              <ul className="text-xs text-red-600 list-disc pl-4 space-y-1">
                {compatibility.reasons.map((reason) => {
                  return <li key={reason}>{t(`reasons.${reason}`)}</li>
                })}
              </ul>
            )}
          </div>
        </div>
      </button>
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      popModal()
    }
  }

  // Conditional content based on state
  let title = t('migrate_project')
  let description = t('select_workspace_to_migrate', { projectName })
  let content: React.ReactNode
  let footer: React.ReactNode

  if (loadingWorkspaces || loadingProductsCount) {
    description = loadingProductsCount
      ? t('analyzing_project_features')
      : t('loading_workspaces')
    content = (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-muted-foreground">{t('loading')}</p>
      </div>
    )
  } else if (productsCountError) {
    description = t('error_analyzing_project')
    content = (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <p className="text-sm text-red-600">
          {t('failed_to_analyze_project_features')}
        </p>
        <Button
          type="button"
          variant="outline_2"
          onClick={() => {
            return window.location.reload()
          }}
        >
          {t('reload')}
        </Button>
      </div>
    )
    footer = (
      <div className="flex gap-2 justify-end w-full">
        <Button
          type="button"
          variant="outline_2"
          onClick={() => {
            return popModal()
          }}
        >
          {t('cancel')}
        </Button>
      </div>
    )
  } else if (showConfirmation) {
    title = t('confirm_migration')
    description = t('please_confirm_the_project_migration')
    content = (
      <div className="space-y-4 py-4">
        <div className="bg-muted p-4 rounded-lg space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">
              {t('project_name')}
            </Label>
            <p className="text-sm font-medium mt-1">{projectName}</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground">
                {t('from')}
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="size-4 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {currentWorkspace?.name || t('unknown_workspace')}
                </p>
              </div>
            </div>

            <ArrowRight className="size-4 text-muted-foreground" />

            <div className="flex-1">
              <Label className="text-xs text-muted-foreground">{t('to')}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="size-4 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {selectedWorkspace?.name || t('unknown_workspace')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('this_action_will_move_the_project_to_the_selected_workspace')}
        </p>
      </div>
    )
    footer = (
      <div className="flex gap-2 justify-end w-full">
        <Button
          type="button"
          variant="outline_2"
          onClick={() => {
            return setShowConfirmation(false)
          }}
          disabled={loading}
        >
          {t('back')}
        </Button>
        <Button
          type="button"
          variant="active"
          onClick={handleMigrate}
          disabled={loading}
        >
          {loading ? t('migrating') : t('confirm_migration')}
        </Button>
      </div>
    )
  } else {
    content = (
      <div className="space-y-4 py-2">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('search_workspaces')}
            value={searchTerm}
            onChange={(e) => {
              return setSearchTerm(e.target.value)
            }}
            className="pl-10"
          />
        </div>

        {/* Workspaces list */}
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {filteredWorkspaces.length > 0 ? (
            filteredWorkspaces.map((workspace) => {
              return renderWorkspaceCard(workspace)
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                {t('no_workspaces_found')}
              </p>
            </div>
          )}
        </div>
      </div>
    )
    footer = (
      <div className="flex gap-2 justify-end w-full">
        <Button
          type="button"
          variant="outline_2"
          onClick={() => {
            return popModal()
          }}
          disabled={loading}
        >
          {t('cancel')}
        </Button>
        <Button
          type="button"
          variant="active"
          onClick={handleMigrate}
          disabled={!selectedWorkspaceId}
        >
          {t('continue')}
        </Button>
      </div>
    )
  }

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      className="max-w-3xl"
      footer={footer}
    >
      {content}
    </ResponsiveModal>
  )
}
